import React, { useEffect, useRef, useState } from "react";
import { Play, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../../utils/api";
import { getFileName } from "../utils/FileName";
import FeedbackSidebar from "../../widgets/FeedbackSidebar";
import DeleteModal from "../../widgets/DeleteModal";

// ───────────────────────────────
// Utility helpers
// ───────────────────────────────
const fileUrl = (p) =>
  `${process.env.REACT_APP_BASE_API_URL}/api/calls/files?path=${encodeURIComponent(p)}`;

function getScoreLevel(score) {
  const n = typeof score === "number" ? score : parseFloat(score);
  if (Number.isNaN(n)) return "na";
  if (n >= 80) return "good";
  if (n >= 60) return "avg";
  return "low";
}

// ───────────────────────────────
// Single feedback card (same visual layout as FeedbackTable)
// ───────────────────────────────
const FeedbackCard = ({ item, onPlay, onViewMore, onDelete }) => {
  const traineeName = item.traineeName || item.traineeId?.name || "-";
  const traineeEmail = item.traineeEmail || item.traineeId?.email || "-";
  const callType = item.callType || "-";
  const feedbackText = item.feedback || "-";
  const score = item.score ?? "-";
  const date = item.createddate
    ? new Date(item.createddate).toLocaleString()
    : "-";
const callId = item.feedbackId || item.callId || "-";

  const scoreLevel = getScoreLevel(score);
  const canPlay =
    item.callRecordFileinfo &&
    (item.artifactTranscriptFilePath || item.transcriberJsonFilePath);

  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
    }
  }, [feedbackText]);

  return (
    <article key={item._id} className="fb-card fb-card--compact">
      {/* Row 1: identity + score */}
      <div className="fb-row fb-row--top">
        <div className="fb-name-email">
          <div className="fb-name" title={traineeName}>
            {traineeName}
          </div>
          <div className="fb-email" title={traineeEmail}>
            {traineeEmail}
          </div>
        </div>
        <div
          className={`fb-score fb-score--${scoreLevel}`}
          title={`Score: ${score}`}
        >
          <span className="fb-score__value">{score}/10</span>
        </div>
      </div>

      {/* Row 2: meta chips */}
      <div className="fb-row fb-row--meta">
        <span className="chip" title="Call Type">
          {callType}
        </span>
        <span className="chip chip--muted" title="Date">
          {date}
        </span>
				<span className="chip chip--info" title="Call ID">
          ID: {callId}
        </span>
      </div>

      {/* Row 3: feedback text */}
      <div className="fb-row fb-row--feedback" style={{ marginBottom: 45 }}>
        <p ref={textRef} className="fb-text">
          {feedbackText}
        </p>

        {isOverflowing && (
          <button
            className="btn btn-secondary"
            style={{
              fontSize: "13px",
              padding: "6px 10px",
              marginTop: "8px",
              borderRadius: "8px",
              textWrap: "nowrap",
            }}
            onClick={() => onViewMore(item)}
          >
            View more
          </button>
        )}
      </div>

      {/* Row 4: Play & Delete buttons */}
      <div className="fb-action" style={{ display: "flex", gap: 10 }}>
        <button
          className="btn btn-play"
          onClick={() => canPlay && onPlay(item)}
          disabled={!canPlay}
          aria-disabled={!canPlay}
          title={canPlay ? "Play & Review" : "Unavailable"}
        >
          <Play size={18} />
          <span className="btn-play__text">Play recording</span>
        </button>

        <button
          className="btn btn-danger"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#FEE2E2",
            border: "1px solid #DC2626",
            color: "#DC2626",
            fontSize: 14,
            padding: "6px 10px",
            borderRadius: 20,
            fontWeight: 500,
          }}
          onClick={() => onDelete(item)}
          title="Delete recording"
        >
          <Trash2 size={16} />
          Delete
        </button>
      </div>
    </article>
  );
};

// ───────────────────────────────
// Feedback list container (Admin version)
// ───────────────────────────────
const FeedbackDashboardRow = ({ items = [], onPlay, onDelete }) => {
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  if (!items || items.length === 0) {
    return (
      <div className="fb-list">
        <div className="fb-empty">No feedback found.</div>
      </div>
    );
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !deleteTarget.feedbackId) {
      toast.error("Cannot delete: Missing feedback ID");
      setDeleteTarget(null);
      return;
    }

    setDeleting(true);
    try {
      const response = await api.delete(
        `api/calls/recordings/${deleteTarget.feedbackId}`
      );
      if (response.ok) {
        toast.success("Recording deleted successfully");
        if (onDelete) onDelete(deleteTarget);
      } else {
        toast.error(response.data?.error || "Failed to delete recording");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the recording");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="fb-list">
        {items.map((item) => (
          <FeedbackCard
            key={item._id}
            item={item}
            onPlay={onPlay}
            onViewMore={setSelectedFeedback}
            onDelete={setDeleteTarget}
          />
        ))}
      </div>

      {/* Sidebar for expanded feedback */}
      {selectedFeedback && (
        <FeedbackSidebar
          item={selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
        />
      )}

      {/* Delete confirmation modal */}
      <DeleteModal
        visible={!!deleteTarget}
        title="Delete Recording"
        message="Are you sure you want to delete this recording? This action cannot be undone."
        confirmLabel={deleting ? "Deleting..." : "Delete"}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        disabled={deleting}
      />
    </>
  );
};

export default FeedbackDashboardRow;
